"use client";

/**
 * Resume-enrollment confirmation (Phase J · Task 57).
 *
 * Resume is an instant signal in the workflow — no body is needed.
 * The modal still exists so the recruiter gets the same confirmation
 * UX as the other actions and can read the consequence ("workflow
 * recomputes next due time"). It is a one-click confirm, not a form.
 */

import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

import { useEnrollmentsList } from "../../hooks/use-enrollments-list";
import type { EnrollmentListItem } from "../../lib/types";

interface ResumeModalProps {
  enrollment: EnrollmentListItem | null;
  jobId: string;
  sequenceId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function ResumeEnrollmentModal({
  enrollment,
  jobId,
  sequenceId,
  open,
  onOpenChange,
}: ResumeModalProps) {
  const { resume } = useEnrollmentsList({ jobId, sequenceId });

  const close = () => {
    if (resume.isPending) return;
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!enrollment) return;
    try {
      await resume.mutateAsync({
        jobId,
        sequenceId,
        enrollmentId: enrollment.id,
      });
      showToast.success("Enrollment resumed");
      onOpenChange(false);
    } catch (err) {
      showToast.error("Couldn't resume the enrollment. Please try again.", {
        description: getApiErrorMessage(err),
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={(next) => (!next ? close() : undefined)} dismissible={!resume.isPending}>
      <ModalContent size="md">
        <ModalHeader title="Resume enrollment?" iconName="IconPlay" />
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            The workflow recomputes the next due time against the
            sender&apos;s current sending schedule and continues from
            the held step.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={close}
            disabled={resume.isPending}
          >
            Cancel
          </Button>
          <SubmitButton
            type="button"
            variant="primary"
            size="sm"
            loading={resume.isPending}
            loadingLabel="Resuming"
            onClick={handleConfirm}
          >
            Resume
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
